/**
 * Datos del panel: hooks de TanStack Query.
 *
 * Todo el panel lee de acá. Ninguna pantalla llama a `fetch` por su cuenta, así
 * que la cache, los reintentos y la invalidación se deciden en un solo lugar.
 *
 * Los roles se cachean 60 s: cambian cuando alguien toca la seguridad, que no es
 * algo que pase entre dos clicks. Los usuarios se invalidan a mano después de
 * cada asignación, que es cuando de verdad cambian.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ListResponse, ModelSpec } from '@/lib/types';
import { contarRegistros, fetchCompanies, fetchRoles, fetchUserMeta, fetchUsers } from '@/panel/api';
import type { PanelUser } from '@/panel/api';
import { saveUserAssignment } from '@/panel/api';
import type { Role } from '@/panel/contract';

export const CLAVES = {
  roles: ['panel', 'roles'] as const,
  usuarios: ['panel', 'usuarios'] as const,
  metaUsuario: ['panel', 'meta', 'base.user'] as const,
  empresas: ['panel', 'empresas'] as const,
  conteo: (modelo: string) => ['panel', 'conteo', modelo] as const,
};

export function useRoles() {
  return useQuery<Role[]>({
    queryKey: CLAVES.roles,
    queryFn: ({ signal }) => fetchRoles(signal),
    staleTime: 60_000,
  });
}

export function useUsuarios() {
  return useQuery<ListResponse<PanelUser>>({
    queryKey: CLAVES.usuarios,
    queryFn: ({ signal }) => fetchUsers(signal),
    staleTime: 30_000,
  });
}

export function useMetaUsuario() {
  return useQuery<ModelSpec>({
    queryKey: CLAVES.metaUsuario,
    queryFn: ({ signal }) => fetchUserMeta(signal),
    staleTime: 5 * 60_000,
  });
}

export function useEmpresas() {
  return useQuery<ListResponse>({
    queryKey: CLAVES.empresas,
    queryFn: ({ signal }) => fetchCompanies(signal),
    staleTime: 5 * 60_000,
  });
}

/** Conteo de registros de un modelo para el tablero de estado. */
export function useConteo(modelo: string, habilitado = true) {
  return useQuery<number>({
    queryKey: CLAVES.conteo(modelo),
    queryFn: ({ signal }) => contarRegistros(modelo, signal),
    staleTime: 60_000,
    enabled: habilitado,
  });
}

/**
 * Guarda roles y empresas de un usuario.
 *
 * Al terminar invalida usuarios Y roles: cambiar quién tiene un rol cambia el
 * «cuánta gente lo tiene» del explorador. Si no se invalidaran los dos, el
 * contador quedaría mintiendo hasta el próximo refresco.
 */
export function useGuardarAsignacion() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      cambios,
    }: {
      id: number;
      cambios: { nucleo_groups?: number[]; companies?: number[] };
    }) => saveUserAssignment(id, cambios),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: CLAVES.usuarios });
      void cliente.invalidateQueries({ queryKey: CLAVES.roles });
    },
  });
}
